import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { admin } from '../api';
import AdminLayout from '../components/AdminLayout';
import StatusBadge from '../components/StatusBadge';
import BusinessStatusBadge from '../components/BusinessStatusBadge';

const formatNumber = (n) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : '-');

const StatCard = ({ label, value }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
    <p className="text-sm text-gray-500">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
  </div>
);

export default function AdminBusinessDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [updating, setUpdating] = useState(false);

  const load = () => admin.business(id).then((r) => setData(r.data)).catch(() => setNotFound(true));

  useEffect(() => { load(); }, [id]);

  const changeStatus = async (status, confirmMessage) => {
    if (!window.confirm(confirmMessage)) return;
    setUpdating(true);
    try {
      await admin.updateBusinessStatus(id, status);
      await load();
    } finally {
      setUpdating(false);
    }
  };

  if (notFound) {
    return (
      <AdminLayout>
        <p className="text-gray-500">Business not found.</p>
      </AdminLayout>
    );
  }

  if (!data) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-40">
          <div className="w-7 h-7 border-4 border-slate-700 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const { business, invoice_count, total_revenue, outstanding, recent_invoices } = data;
  const currentStatus = business.status || 'active';

  return (
    <AdminLayout>
      <Link to="/admin/dashboard" className="text-sm text-primary-600 hover:underline">← Back to Businesses</Link>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-3 mb-2">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-gray-900">{business.name}</h1>
          <BusinessStatusBadge status={business.status} />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={updating || currentStatus === 'suspended'}
            onClick={() => changeStatus('suspended', `Suspend ${business.name}? They will be unable to log in until reactivated.`)}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Suspend
          </button>
          <button
            type="button"
            disabled={updating || currentStatus === 'inactive'}
            onClick={() => changeStatus('inactive', `Deactivate ${business.name}? They will be unable to log in until reactivated.`)}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-yellow-200 text-yellow-700 hover:bg-yellow-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Deactivate
          </button>
          <button
            type="button"
            disabled={updating || currentStatus === 'active'}
            onClick={() => changeStatus('active', `Reactivate ${business.name}? They will be able to log in again immediately.`)}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-green-200 text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reactivate
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-400 mb-6">Business data is read-only — only account status can be changed above.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h2 className="text-sm font-semibold text-gray-500 mb-3">Business Info</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-gray-500">Country</dt><dd className="text-gray-900">{business.country || '-'}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Address</dt><dd className="text-gray-900 text-right">{business.address || '-'}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Phone</dt><dd className="text-gray-900">{business.phone || '-'}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Date Joined</dt><dd className="text-gray-900">{formatDate(business.created_at)}</dd></div>
          </dl>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h2 className="text-sm font-semibold text-gray-500 mb-3">Owner Info</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-gray-500">Email</dt><dd className="text-gray-900">{business.owner_email || '—'}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Business Email</dt><dd className="text-gray-900">{business.email || '-'}</dd></div>
          </dl>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard label="Invoice Count" value={invoice_count} />
        <StatCard label="Total Revenue" value={formatNumber(total_revenue)} />
        <StatCard label="Outstanding" value={formatNumber(outstanding)} />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Invoices</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 text-gray-500 font-medium">Invoice</th>
                <th className="text-left py-2 text-gray-500 font-medium">Client</th>
                <th className="text-left py-2 text-gray-500 font-medium">Date</th>
                <th className="text-right py-2 text-gray-500 font-medium">Amount</th>
                <th className="text-center py-2 text-gray-500 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {recent_invoices.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-gray-400">No invoices yet</td></tr>
              )}
              {recent_invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-gray-50">
                  <td className="py-3 text-gray-900 font-medium">{inv.invoice_number}</td>
                  <td className="py-3 text-gray-600">{inv.client_name}</td>
                  <td className="py-3 text-gray-500">{formatDate(inv.invoice_date)}</td>
                  <td className="py-3 text-right text-gray-900">{formatNumber(inv.total_amount)}</td>
                  <td className="py-3 text-center"><StatusBadge status={inv.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
